import React, { useMemo, useState } from "react";
import { Alert, Avatar, Box, Button, Chip, Paper, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import DashboardShell from "../../../../components/DashboardShell";
import Loader from "../../../../components/Loader";
import useContractorDashboard from "../../../../hooks/useContractorDashboard";
import { contractorStatusLabel, normalizeContractorStatus } from "../../../../libs/contractorReview";
import contractorDashboardNav from "./dashboardNav";
import BillingSection from "./sections/BillingSection";
import EarningsSection from "./sections/EarningsSection";
import OpportunitiesSection from "./sections/OpportunitiesSection";
import OverviewSection from "./sections/OverviewSection";
import WorkSection from "./sections/WorkSection";

const PendingApprovalCard = ({ profile }) => {
    const statusLabel = contractorStatusLabel(profile);
    const changesRequested = normalizeContractorStatus(profile?.onboarding_status || profile?.review?.status) === "changes_requested";
    const publicMessage = profile?.review?.public_message || "No action is needed from you right now. We will contact you if an interview or additional details are needed.";
    const checks = [
        { icon: <DescriptionOutlinedIcon fontSize="small" />, label: "Work profile", helper: "We review your resume and generated work profile for matching." },
        { icon: <TaskAltOutlinedIcon fontSize="small" />, label: "Candidate screening", helper: "Our team will review your work profile and begin the vetting process." },
        { icon: <AdminPanelSettingsOutlinedIcon fontSize="small" />, label: "Final approval", helper: "Approved contractors become visible in matching and hire flows." },
    ];

    return (
        <Box sx={{ width: "100%", maxWidth: 980 }}>
            <Paper
                variant="outlined"
                sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    bgcolor: "background.paper",
                    boxShadow: "0 12px 32px rgba(60,64,67,0.08)",
                }}
            >
                <Box
                    sx={{
                        px: { xs: 2, sm: 3 },
                        py: { xs: 2.5, sm: 3 },
                        background: "linear-gradient(135deg, rgba(227,242,253,0.95), rgba(248,250,252,0.98))",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }}>
                        <Avatar
                            variant="rounded"
                            sx={{
                                width: 56,
                                height: 56,
                                bgcolor: "primary.main",
                                color: "primary.contrastText",
                                borderRadius: 2,
                            }}
                        >
                            <HourglassTopOutlinedIcon />
                        </Avatar>
                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography variant="h5" sx={{ fontWeight: 850 }}>
                                    {changesRequested ? "We requested few profile changes" : "Your contractor profile is under review"}
                                </Typography>
                                <Chip size="small" color="warning" label={statusLabel} sx={{ borderRadius: 1, fontWeight: 700 }} />
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 760 }}>
                                {changesRequested
                                    ? "Update your profile and resubmit it for admin review."
                                    : "Our team must approve your profile before the workspace is unlocked."}
                            </Typography>
                        </Box>
                        {changesRequested && (
                            <Button
                                component={RouterLink}
                                to="/contractor/onboarding"
                                variant="contained"
                                size="small"
                                sx={{ textTransform: "none", fontWeight: 800, borderRadius: 1.5, whiteSpace: "nowrap" }}
                            >
                                Update profile
                            </Button>
                        )}
                    </Stack>
                </Box>

                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                    <Stack spacing={2.5}>
                        <Alert severity="info" icon={<LockOutlinedIcon fontSize="inherit" />} sx={{ borderRadius: 1.5 }}>
                            {publicMessage}
                        </Alert>

                        <Stack spacing={1.25}>
                            {checks.map((item) => (
                                <Paper key={item.label} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Avatar variant="rounded" sx={{ width: 34, height: 34, bgcolor: "grey.100", color: "primary.main" }}>
                                            {item.icon}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                                {item.label}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {item.helper}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            ))}
                        </Stack>
                    </Stack>
                </Box>
            </Paper>
        </Box>
    );
};

const Dashboard = () => {
    const { accountExist, approved, loading, navBadges, overview, role, token } = useContractorDashboard();
    const [activeSection, setActiveSection] = useState("overview");
    const sidebarItems = useMemo(
        () => contractorDashboardNav.map((item) => ({ ...item, badge: navBadges?.[item.key] || 0 })),
        [navBadges]
    );

    return (
      <>
        {(loading) ? (
            <Loader variant="fullscreen" open={loading} label="Loading contractor dashboard..." />
        ):( 
            <>
                {role !== "contractor" && (
                    <Box sx={{ p: 2, width: "100%" }}>
                        <Alert severity="info">This dashboard is available only for contractor accounts.</Alert>
                    </Box>
                )}
                {role === "contractor" && accountExist && !approved && (
                    <DashboardShell
                        title="Contractor Workspace"
                        items={sidebarItems.map((item) => ({ ...item, disabled: true, badge: 0 }))}
                        activeKey="overview"
                        onSelect={() => {}}
                        headerless
                    >
                        <PendingApprovalCard profile={token} />
                    </DashboardShell>
                )}
                {accountExist &&
                approved &&
                role === "contractor" &&
                    <DashboardShell
                        title="Contractor Workspace"
                        items={sidebarItems}
                        activeKey={activeSection}
                        onSelect={setActiveSection}
                        headerless
                    >
                        {activeSection === "overview" && <OverviewSection overview={overview} accountId={token?.id}/>}
                        {activeSection === "opportunities" && <OpportunitiesSection />}
                        {activeSection === "work" && <WorkSection />}
                        {activeSection === "earnings" && <EarningsSection accountId={token?.id} />}
                        {activeSection === "billing" && <BillingSection accountId={token?.id} />}
                    </DashboardShell>
                }
            </>
        )}
      </>
    );
}

export default Dashboard;
